import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MyTasksPage() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const { theme } = useTheme();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/player/tasks`, {
        headers: getAuthHeader()
      });
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ 
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)' 
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <div className={`backdrop-blur-md ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'} border-b ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button onClick={() => navigate('/player-dashboard')} className={`flex items-center ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} mb-4`}>
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
          <h1 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>My Tasks & Drills</h1>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-2`}>Assignments from your coach</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-16"><div className="spinner"></div></div>
        ) : tasks.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {tasks.map((task) => (
              <Card key={task.id} className={`glass-card ${theme === 'light' ? 'bg-white/95' : ''}`}>
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className={`text-xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{task.title}</h3>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{task.description}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs ${task.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
                      {task.status}
                    </span>
                  </div>
                  
                  {task.due_date && (
                    <div className="flex items-center space-x-2 text-sm">
                      <Clock className="w-4 h-4 text-orange-400" />
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                        Due: {new Date(task.due_date).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  
                  {task.requires_video && (
                    <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-blue-50 border border-blue-200'}`}>
                      <AlertCircle className="w-4 h-4 inline mr-2 text-blue-400" />
                      <span className={`text-sm ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}>Video evidence required</span>
                    </div>
                  )}
                  
                  {task.status !== 'completed' && (
                    <Button size="sm" className="w-full bg-gradient-to-r from-green-500 to-emerald-600">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark Complete
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>No tasks assigned yet</p>
          </div>
        )}
      </div>
    </div>
  );
}
